/**
 * Business Catalog Page - Global Business Directory
 * Lists all businesses (CLAIMED and UNCLAIMED) with search and filters
 * Route: /businesses
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  MapPin,
  Phone,
  Globe,
  ExternalLink,
  Filter,
  X,
  Loader2,
  Building2,
  CheckCircle2,
  AlertCircle,
  Map,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import GlobalNav from '@/components/GlobalNav';
import Footer from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// i18n labels
const LABELS = {
  et: {
    title: 'Ettevõtete kataloog',
    subtitle: 'Leia ettevõtteid üle Euroopa',
    search: 'Otsi ettevõtteid...',
    filterCountry: 'Riik',
    filterCity: 'Linn',
    filterCategory: 'Kategooria',
    filterStatus: 'Staatus',
    allCountries: 'Kõik riigid',
    allCategories: 'Kõik kategooriad',
    allStatuses: 'Kõik',
    claimed: 'Kinnitatud',
    unclaimed: 'Kinnitamata',
    verified: 'Verifitseeritud',
    claimBusiness: 'Kinnita see ettevõte',
    viewOnMap: 'Vaata kaardil',
    viewDetails: 'Vaata lähemalt',
    noResults: 'Ettevõtteid ei leitud',
    tryDifferent: 'Proovi teisi filtreid',
    results: 'tulemust',
    page: 'Lehekülg',
    of: '/',
    loading: 'Laadimine...'
  },
  en: {
    title: 'Business Catalog',
    subtitle: 'Find businesses across Europe',
    search: 'Search businesses...',
    filterCountry: 'Country',
    filterCity: 'City',
    filterCategory: 'Category',
    filterStatus: 'Status',
    allCountries: 'All countries',
    allCategories: 'All categories',
    allStatuses: 'All',
    claimed: 'Claimed',
    unclaimed: 'Unclaimed',
    verified: 'Verified',
    claimBusiness: 'Claim this business',
    viewOnMap: 'View on Map',
    viewDetails: 'View Details',
    noResults: 'No businesses found',
    tryDifferent: 'Try different filters',
    results: 'results',
    page: 'Page',
    of: 'of',
    loading: 'Loading...'
  },
  fi: {
    title: 'Yrityskatalogi',
    subtitle: 'Löydä yrityksiä Euroopasta',
    search: 'Etsi yrityksiä...',
    filterCountry: 'Maa',
    filterCity: 'Kaupunki',
    filterCategory: 'Kategoria',
    filterStatus: 'Tila',
    allCountries: 'Kaikki maat',
    allCategories: 'Kaikki kategoriat',
    allStatuses: 'Kaikki',
    claimed: 'Vahvistettu',
    unclaimed: 'Vahvistamaton',
    verified: 'Vahvistettu',
    claimBusiness: 'Vahvista tämä yritys',
    viewOnMap: 'Näytä kartalla',
    viewDetails: 'Näytä tiedot',
    noResults: 'Yrityksiä ei löytynyt',
    tryDifferent: 'Kokeile eri suodattimia',
    results: 'tulosta',
    page: 'Sivu',
    of: '/',
    loading: 'Ladataan...'
  },
  ru: {
    title: 'Каталог компаний',
    subtitle: 'Найдите компании по всей Европе',
    search: 'Поиск компаний...',
    filterCountry: 'Страна',
    filterCity: 'Город',
    filterCategory: 'Категория',
    filterStatus: 'Статус',
    allCountries: 'Все страны',
    allCategories: 'Все категории',
    allStatuses: 'Все',
    claimed: 'Подтверждено',
    unclaimed: 'Не подтверждено',
    verified: 'Верифицировано',
    claimBusiness: 'Подтвердить компанию',
    viewOnMap: 'Показать на карте',
    viewDetails: 'Подробнее',
    noResults: 'Компании не найдены',
    tryDifferent: 'Попробуйте другие фильтры',
    results: 'результатов',
    page: 'Страница',
    of: 'из',
    loading: 'Загрузка...'
  },
  nb: {
    title: 'Bedriftskatalog',
    subtitle: 'Finn bedrifter i hele Europa',
    search: 'Søk etter bedrifter…',
    filterCountry: 'Land',
    filterCity: 'By',
    filterCategory: 'Kategori',
    filterStatus: 'Status',
    allCountries: 'Alle land',
    allCategories: 'Alle kategorier',
    allStatuses: 'Alle',
    claimed: 'Bekreftet',
    unclaimed: 'Ikke bekreftet',
    verified: 'Verifisert',
    claimBusiness: 'Krev denne bedriften',
    viewOnMap: 'Vis på kart',
    viewDetails: 'Vis detaljer',
    noResults: 'Ingen bedrifter funnet',
    tryDifferent: 'Prøv andre filtre',
    results: 'treff',
    page: 'Side',
    of: 'av',
    loading: 'Laster…'
  },
  sv: {
    title: 'Företagskatalog',
    subtitle: 'Hitta företag i hela Europa',
    search: 'Sök efter företag…',
    filterCountry: 'Land',
    filterCity: 'Stad',
    filterCategory: 'Kategori',
    filterStatus: 'Status',
    allCountries: 'Alla länder',
    allCategories: 'Alla kategorier',
    allStatuses: 'Alla',
    claimed: 'Bekräftad',
    unclaimed: 'Ej bekräftad',
    verified: 'Verifierad',
    claimBusiness: 'Gör anspråk på företaget',
    viewOnMap: 'Visa på karta',
    viewDetails: 'Visa detaljer',
    noResults: 'Inga företag hittades',
    tryDifferent: 'Pröva andra filter',
    results: 'träffar',
    page: 'Sida',
    of: 'av',
    loading: 'Laddar…'
  }
};

const CATEGORY_LABELS = {
  services: { et: 'Teenused', en: 'Services', fi: 'Palvelut', ru: 'Услуги', nb: 'Tjenester', sv: 'Tjänster' },
  auto_repair: { et: 'Autoremont', en: 'Auto Repair', fi: 'Autokorjaamo', ru: 'Автосервис', nb: 'Bilverksted', sv: 'Bilreparation' },
  b2b: { et: 'Ehitus/B2B', en: 'Construction/B2B', fi: 'Rakennus/B2B', ru: 'Строительство/B2B', nb: 'Bygg / B2B', sv: 'Bygg / B2B' },
  transport: { et: 'Transport', en: 'Transport', fi: 'Kuljetus', ru: 'Транспорт', nb: 'Transport', sv: 'Transport' },
  accommodation: { et: 'Majutus', en: 'Accommodation', fi: 'Majoitus', ru: 'Жильё', nb: 'Overnatting', sv: 'Boende' },
  equipment: { et: 'Seadmed', en: 'Equipment', fi: 'Laitteet', ru: 'Оборудование', nb: 'Utstyr', sv: 'Utrustning' }
};

// Business Card Component
function BusinessCard({ business, labels, language }) {
  const { t } = useLanguage();
  const isClaimed = business.status === 'CLAIMED';
  
  return (
    <Card 
      className={`transition-all hover:shadow-lg ${
        isClaimed ? 'border-green-200' : 'border-gray-200'
      }`}
      data-testid={`business-card-${business.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Header with status */}
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg truncate">{business.name}</h3>
              {isClaimed ? (
                <Badge className="bg-green-100 text-green-700 text-xs">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {labels.claimed}
                </Badge>
              ) : (
                <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {labels.unclaimed}
                </Badge>
              )}
              {business.is_verified && (
                <Badge className="bg-blue-100 text-blue-700 text-xs">
                  {labels.verified}
                </Badge>
              )}
            </div>
            
            {/* Location */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <MapPin className="w-4 h-4 text-orange-500" />
              <span>
                {business.city || business.region || 'Unknown'}, {business.country_code}
              </span>
            </div>
            
            {/* Categories */}
            <div className="flex flex-wrap gap-1 mb-3">
              {business.categories?.slice(0, 3).map((cat, i) => {
                // Try BusinessCatalogPage's local CATEGORY_LABELS first,
                // then fall back to global categories.* i18n namespace,
                // then humanize the raw id (replace _ with space, title-case).
                const local = CATEGORY_LABELS[cat]?.[language];
                const fromI18n = (() => {
                  const k = `categories.${cat}`;
                  const v = (typeof t === 'function') ? t(k) : k;
                  return v && v !== k && !v.startsWith?.('[MISSING:') ? v : null;
                })();
                const humanized = String(cat || '').split('_')
                  .map(w => w ? w[0].toUpperCase() + w.slice(1) : '')
                  .join(' ');
                return (
                  <Badge key={i} variant="outline" className="text-xs">
                    {local || fromI18n || humanized}
                  </Badge>
                );
              })}
            </div>
            
            {/* Contact info (only for claimed) */}
            {isClaimed && (
              <div className="space-y-1 text-sm text-muted-foreground">
                {business.phone_public && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-3 h-3" />
                    <span>{business.phone_public}</span>
                  </div>
                )}
                {business.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="w-3 h-3" />
                    <a 
                      href={business.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate max-w-[200px]"
                    >
                      {business.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="mt-4 flex gap-2">
          <Button 
            size="sm" 
            className="flex-1"
            asChild
          >
            <Link to={`/business/${business.id}`}>
              {labels.viewDetails}
              <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          </Button>
          
          {!isClaimed && (
            <Button 
              size="sm" 
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
              asChild
            >
              <Link to={`/business/${business.id}?claim=true`}>
                {labels.claimBusiness}
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function BusinessCatalogPage() {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const labels = LABELS[language] || LABELS.en;
  
  // State
  const [businesses, setBusinesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;
  
  // Filters from URL
  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    country_code: searchParams.get('country') || '',
    city: searchParams.get('city') || '',
    category: searchParams.get('category') || '',
    status: searchParams.get('status') || ''
  });
  
  // Fetch filter options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [catRes, countryRes] = await Promise.all([
          fetch(`${API_URL}/api/businesses/categories`),
          fetch(`${API_URL}/api/businesses/countries`)
        ]);
        
        if (catRes.ok) {
          const catData = await catRes.json();
          setCategories(catData.categories || []);
        }
        
        if (countryRes.ok) {
          const countryData = await countryRes.json();
          setCountries(countryData.countries || []);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    
    fetchOptions();
  }, []);
  
  // Fetch businesses
  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (filters.q) params.append('q', filters.q);
      if (filters.country_code) params.append('country_code', filters.country_code);
      if (filters.city) params.append('city', filters.city);
      if (filters.category) params.append('categories', filters.category);
      if (filters.status) params.append('status', filters.status);
      params.append('limit', limit.toString());
      params.append('offset', ((page - 1) * limit).toString());
      
      const response = await fetch(`${API_URL}/api/businesses/search?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);
  
  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);
  
  // Update URL when filters change
  const updateFilters = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPage(1);
    
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k === 'q' ? 'q' : k === 'country_code' ? 'country' : k, v);
    });
    setSearchParams(params);
  };
  
  const clearFilters = () => {
    setFilters({ q: '', country_code: '', city: '', category: '', status: '' });
    setSearchParams({});
    setPage(1);
  };
  
  const totalPages = Math.ceil(total / limit);
  const hasActiveFilters = Object.values(filters).some(v => v);
  
  return (
    <>
      <SEOHead 
        title={`${labels.title} | KEIKKA`}
        description={labels.subtitle}
      />
      
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <GlobalNav />
        
        <main className="flex-1">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white py-12">
            <div className="max-w-6xl mx-auto px-4">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-8 h-8" />
                <h1 className="text-3xl font-bold">{labels.title}</h1>
              </div>
              <p className="text-orange-100">{labels.subtitle}</p>
              
              {/* Quick stats */}
              <div className="mt-4 flex gap-6 text-sm">
                <span>{total} {labels.results}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20"
                  asChild
                >
                  <Link to="/map?layer=businesses">
                    <Map className="w-4 h-4 mr-2" />
                    {labels.viewOnMap}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
          
          {/* Filters */}
          <div className="bg-white border-b sticky top-0 z-10">
            <div className="max-w-6xl mx-auto px-4 py-4">
              <div className="flex flex-wrap gap-3 items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder={labels.search}
                    value={filters.q}
                    onChange={(e) => updateFilters('q', e.target.value)}
                    className="pl-10"
                    data-testid="business-search-input"
                  />
                </div>
                
                {/* Country */}
                <Select
                  value={filters.country_code}
                  onValueChange={(v) => updateFilters('country_code', v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="w-[150px]" data-testid="filter-country">
                    <SelectValue placeholder={labels.filterCountry} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{labels.allCountries}</SelectItem>
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} ({c.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Category */}
                <Select
                  value={filters.category}
                  onValueChange={(v) => updateFilters('category', v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="w-[160px]" data-testid="filter-category">
                    <SelectValue placeholder={labels.filterCategory} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{labels.allCategories}</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {CATEGORY_LABELS[c.id]?.[language] || c.name} ({c.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Status */}
                <Select
                  value={filters.status}
                  onValueChange={(v) => updateFilters('status', v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="w-[140px]" data-testid="filter-status">
                    <SelectValue placeholder={labels.filterStatus} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{labels.allStatuses}</SelectItem>
                    <SelectItem value="CLAIMED">{labels.claimed}</SelectItem>
                    <SelectItem value="UNCLAIMED">{labels.unclaimed}</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Clear filters */}
                {hasActiveFilters && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={clearFilters}
                    data-testid="clear-filters"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Results */}
          <div className="max-w-6xl mx-auto px-4 py-8">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2 mb-4" />
                      <Skeleton className="h-8 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : businesses.length === 0 ? (
              <div className="text-center py-16">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold mb-2">{labels.noResults}</h3>
                <p className="text-muted-foreground mb-4">{labels.tryDifferent}</p>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {businesses.map((business) => (
                    <BusinessCard 
                      key={business.id} 
                      business={business} 
                      labels={labels}
                      language={language}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <span className="text-sm text-muted-foreground">
                      {labels.page} {page} {labels.of} {totalPages}
                    </span>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
